// src/components/Faq.js - Fixed version with proper contact form handling
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ResidentSidebar from './ResidentSidebar';
import FaqContactForm from './FaqContactForm';
import '../css/Faq.css';
import '../css/FaqContactForm.css';
import { trackEvent } from './AnalyticsTracker';

const Faq = () => {
  const navigate = useNavigate();
  const [faqData, setFaqData] = useState({});
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'nl');
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormContext, setContactFormContext] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Ref for the answer content container
  const answerContentRef = useRef(null);
  
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchFaqData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/faq`);
        if (!response.ok) {
          throw new Error('Error fetching FAQ data');
        }
        const data = await response.json();
        
        setFaqData(data);
      } catch (err) {
        console.error('Error fetching FAQ:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFaqData();
  }, [API_URL]);

  // Handle contact form buttons - this runs after the content is rendered
  useEffect(() => {
    if (answerContentRef.current && selectedQuestion) {
      // Find all contact form trigger buttons in the rendered content
      const contactButtons = answerContentRef.current.querySelectorAll('.contact-form-trigger, a[href="#contact"]');
      
      contactButtons.forEach(button => {
        // Remove any existing click handlers to prevent duplicates
        button.removeEventListener('click', handleContactButtonClick);
        
        // Add the click handler
        button.addEventListener('click', handleContactButtonClick);
        
        // Make sure it looks like a button
        button.style.cursor = 'pointer';
        
        // Prevent default link behavior
        button.addEventListener('click', (e) => {
          e.preventDefault();
        });
      });
      
      // Cleanup function
      return () => {
        contactButtons.forEach(button => {
          button.removeEventListener('click', handleContactButtonClick);
        });
      };
    }
  }, [selectedQuestion, selectedSubject]); // Re-run when question changes

  // Contact button click handler
  const handleContactButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Contact button clicked!');
    
    // Create context based on current FAQ item
    const faqContext = {
      subject: selectedSubject,
      question: selectedQuestion?.question || 'General question'
    };
    
    handleContactFormOpen(faqContext);
  };

  // Track when a user clicks on a FAQ question
  const handleQuestionClick = (subject, question) => {
    try {
      trackEvent('view_faq_item', { 
        subject,
        question: question.question || question,
        faqId: `${subject}-${(question.question || question).substring(0, 20)}`
      });
      
      setSelectedQuestion(question);
    } catch (error) {
      console.error("Error tracking FAQ click:", error);
      setSelectedQuestion(question);
    }
  };

  // Track when a user selects a FAQ subject
  const handleSubjectClick = (subject) => {
    try {
      trackEvent('view_faq_subject', { 
        subject
      });
      
      setSelectedSubject(subject);
    } catch (error) {
      console.error("Error tracking subject click:", error);
      setSelectedSubject(subject);
    }
  };

  // Handle opening contact form with FAQ context
  const handleContactFormOpen = (faqContext = null) => {
    setContactFormContext(faqContext);
    setShowContactForm(true);
    
    // Track contact form opening
    try {
      trackEvent('open_faq_contact_form', {
        hasContext: !!faqContext,
        subject: faqContext?.subject,
        question: faqContext?.question ? faqContext.question.substring(0, 50) : null
      });
    } catch (error) {
      console.error("Error tracking contact form open:", error);
    }
  };

  // Handle closing contact form
  const handleContactFormClose = () => {
    setShowContactForm(false);
    setContactFormContext(null);
  };

  // Track when language changes
  useEffect(() => {
    if (language) {
      try {
        trackEvent('change_language', { language });
      } catch (error) {
        console.error("Error tracking language change:", error);
      }
    }
    
    setSelectedSubject(null);
    setSelectedQuestion(null);
    setSearchQuery('');
    localStorage.setItem('language', language);
  }, [language]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    const langData = faqData[language] || {};
    const hits = [];
    Object.entries(langData).forEach(([subject, questions]) => {
      (questions || []).forEach((item) => {
        const questionText = (item.question || '').toLowerCase();
        const answerText = (item.answer || '').replace(/<[^>]+>/g, ' ').toLowerCase();
        if (
          subject.toLowerCase().includes(q) ||
          questionText.includes(q) ||
          answerText.includes(q)
        ) {
          hits.push({ subject, item });
        }
      });
    });
    return hits;
  }, [searchQuery, faqData, language]);

  const handleSearchResultClick = (subject, item) => {
    try {
      trackEvent('faq_search_result_click', {
        query: searchQuery.substring(0, 50),
        subject,
        question: (item.question || '').substring(0, 50),
      });
    } catch (e) {
      console.error('Error tracking search result:', e);
    }
    setSelectedSubject(subject);
    setSelectedQuestion(item);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedSubject(null);
    setSelectedQuestion(null);
  };

  // Process FAQ answer content - simplified version without setTimeout
  const processAnswerContent = (content, faqContext) => {
    if (!content) return '';
    
    // Replace Google Form links and other external form links with contact form triggers
    let processedContent = content.replace(
      /<a[^>]*href="https:\/\/forms\.gle\/[^"]*"[^>]*>(.*?)<\/a>/gi,
      (match, buttonText) => {
        return `<a href="#contact" class="faq-button contact-form-trigger">${buttonText}</a>`;
      }
    );
    
    // Also handle any other contact form triggers that might already be in the content
    processedContent = processedContent.replace(
      /<a[^>]*class="[^"]*contact-form-trigger[^"]*"[^>]*>/gi,
      (match) => {
        // Ensure the href is set correctly
        return match.replace(/href="[^"]*"/, 'href="#contact"');
      }
    );
    
    return processedContent;
  };

  // Translations for UI text
  const t = {
    nl: {
      loading: "Bezig met laden...",
      error: "Fout",
      backToSubjects: "Terug naar onderwerpen",
      backToQuestions: "Terug naar vragen",
      backToHome: "Terug naar home",
      title: "Veelgestelde vragen",
      noFaqAvailable: "Geen FAQ beschikbaar. Probeer het later opnieuw.",
      contactButton: "Stel een vraag over dit onderwerp",
      searchPlaceholder: "Zoek in vragen en antwoorden...",
      searchClear: "Wissen",
      searchResultsTitle: "Zoekresultaten",
      searchNoResults: "Geen resultaten gevonden voor",
      searchResultCount: (n) => `${n} resultaat${n === 1 ? '' : 'en'}`,
      backToResults: "Terug naar zoekresultaten"
    },
    en: {
      loading: "Loading...",
      error: "Error",
      backToSubjects: "Back to subjects",
      backToQuestions: "Back to questions",
      backToHome: "Back to home",
      title: "Frequently Asked Questions",
      translationDisclaimer: "English translations are provided for convenience",
      noFaqAvailable: "No FAQ available. Please try again later.",
      contactButton: "Ask a question about this topic",
      searchPlaceholder: "Search questions and answers...",
      searchClear: "Clear",
      searchResultsTitle: "Search results",
      searchNoResults: "No results found for",
      searchResultCount: (n) => `${n} result${n === 1 ? '' : 's'}`,
      backToResults: "Back to search results"
    }
  };

  // Content to render
  const renderContent = () => {
    if (loading) return <p className="loading-message">{t[language].loading}</p>;
    if (error) return <p className="error-message">{t[language].error}: {error}</p>;

    const currentLanguageData = faqData[language] || {};
    const subjects = Object.keys(currentLanguageData);
    
    if (subjects.length === 0) {
      return <p className="no-data-message">{t[language].noFaqAvailable}</p>;
    }

    if (searchResults && !selectedQuestion) {
      return (
        <div className="faq-search-results">
          <h2>{t[language].searchResultsTitle}</h2>
          {searchResults.length === 0 ? (
            <p className="no-data-message">
              {t[language].searchNoResults} "{searchQuery}"
            </p>
          ) : (
            <>
              <p className="faq-search-count">{t[language].searchResultCount(searchResults.length)}</p>
              <ul>
                {searchResults.map((hit, idx) => (
                  <li key={`${hit.subject}-${hit.item.id || idx}`}>
                    <button onClick={() => handleSearchResultClick(hit.subject, hit.item)}>
                      <span className="faq-search-subject">{hit.subject}</span>
                      <span className="faq-search-question">{hit.item.question}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      );
    }

    if (!selectedSubject) {
      return (
        <ul className="faq-subjects-list">
          {subjects.map((subject, index) => (
            <li key={index}>
              <button onClick={() => handleSubjectClick(subject)}>
                {subject}
              </button>
            </li>
          ))}
        </ul>
      );
    } else if (!selectedQuestion) {
      const questions = currentLanguageData[selectedSubject] || [];
      
      return (
        <div className="faq-questions">
          <h2>{selectedSubject}</h2>
          {questions.length > 0 ? (
            <>
              <ul>
                {questions.map((item, index) => (
                  <li key={item.id || index}>
                    <button onClick={() => handleQuestionClick(selectedSubject, item)}>
                      {item.question}
                    </button>
                  </li>
                ))}
              </ul>
              
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button 
                  className="faq-button contact-form-trigger"
                  onClick={() => handleContactFormOpen({ 
                    subject: selectedSubject, 
                    question: `Algemene vraag over ${selectedSubject}` 
                  })}
                >
                  {t[language].contactButton}
                </button>
              </div>
            </>
          ) : (
            <p>No questions available for this subject.</p>
          )}
          <button className="back-button" onClick={() => setSelectedSubject(null)}>
            {t[language].backToSubjects}
          </button>
        </div>
      );
    } else {
      const faqContext = {
        subject: selectedSubject,
        question: selectedQuestion.question
      };
      
      return (
        <div className="faq-answer">
          <h2>{selectedQuestion.question}</h2>
          <div 
            ref={answerContentRef}
            className="answer-content" 
            dangerouslySetInnerHTML={{ 
              __html: processAnswerContent(selectedQuestion.answer, faqContext) 
            }}
          />
          <button className="back-button" onClick={() => setSelectedQuestion(null)}>
            {searchResults ? t[language].backToResults : t[language].backToQuestions}
          </button>
        </div>
      );
    }
  };

  const showSearchBar = !loading && !error && Object.keys(faqData[language] || {}).length > 0;

  return (
    <div className="page-with-sidebar">
      <ResidentSidebar language={language} setLanguage={setLanguage} />
      
      <div className="main-content faq-page-content">
        <div className="faq-container">
          <h1>{t[language].title}</h1>
          
          {language === 'en' && (
            <p className="translation-disclaimer">{t[language].translationDisclaimer}</p>
          )}

          {showSearchBar && (
            <div className="faq-search-bar">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t[language].searchPlaceholder}
                className="faq-search-input"
                aria-label={t[language].searchPlaceholder}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="faq-search-clear"
                  onClick={clearSearch}
                  aria-label={t[language].searchClear}
                >
                  ×
                </button>
              )}
            </div>
          )}

          {renderContent()}
          
          <div className="back-to-home-container">
            <button 
              onClick={() => navigate('/home')} 
              className="back-to-home-button"
            >
              {language === 'nl' ? 'Terug naar home' : 'Back to home'}
            </button>
          </div>
        </div>
        
        <footer className="page-footer">
          <p>&copy; {new Date().getFullYear()} VVE Polderzicht • <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
        </footer>
      </div>
      
      {/* Contact Form Modal */}
      <FaqContactForm
        isOpen={showContactForm}
        onClose={handleContactFormClose}
        faqContext={contactFormContext}
        language={language}
      />
    </div>
  );
};

export default Faq;